import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Bomb, Check, AlertTriangle, ArrowRight, Pencil, CheckCircle2 } from "lucide-react";
import axios from "axios";
import { Btn, Card, Input, Label, Spinner, StatusBadge, Textarea } from "@/components/ui-kit";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import PdfProofViewer from "@/components/PdfProofViewer";
import { API } from "@/lib/api";
import { formatDateUK, formatFileSize } from "@/lib/format";

const publicApi = axios.create({ baseURL: API });

const NAME_KEY = "kerboom_proof_name";

const ProofPage = () => {
  const { token } = useParams();
  const [activeToken, setActiveToken] = useState(token);
  const [proof, setProof] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [authorName, setAuthorName] = useState(() => localStorage.getItem(NAME_KEY) || "");
  const [showNameModal, setShowNameModal] = useState(false);
  const [decisionLoading, setDecisionLoading] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [thanksModal, setThanksModal] = useState(null); // "approved" | "changes_requested" | null

  const fileUrl = `${API}/public/proofs/${activeToken}/file`;
  // Base URL for serving images attached to comments. Suffix `/{attachment_id}`
  // resolves to a raw image response from the backend.
  const attachmentBaseUrl = `${API}/public/proofs/${activeToken}/attachments`;

  const load = async (t = activeToken) => {
    setLoading(true);
    try {
      const { data } = await publicApi.get(`/public/proofs/${t}`);
      setProof(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load this proof.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(activeToken); /* eslint-disable-next-line */ }, [activeToken]);

  useEffect(() => {
    if (!authorName && proof && !proof.archived) setShowNameModal(true);
  }, [authorName, proof]);

  const saveName = (n) => {
    const trimmed = n.trim();
    if (!trimmed) return;
    setAuthorName(trimmed);
    localStorage.setItem(NAME_KEY, trimmed);
    setShowNameModal(false);
  };

  const addComment = async (page, x, y, text) => {
    const { data } = await publicApi.post(`/public/proofs/${activeToken}/comments`, {
      page, x, y, text, author_name: authorName,
    });
    setProof((p) => ({ ...p, comments: [...(p.comments || []), data] }));
    // Return the new comment so the caller can chain attachment uploads.
    return data;
  };

  const addAnnotation = async (page, points, color, text) => {
    const { data } = await publicApi.post(`/public/proofs/${activeToken}/annotations`, {
      page, points, color, text, author_name: authorName,
    });
    setProof((p) => ({ ...p, annotations: [...(p.annotations || []), data] }));
  };

  const deleteAnnotation = async (id) => {
    await publicApi.delete(`/public/proofs/${activeToken}/annotations/${id}`, {
      params: { author_name: authorName },
    });
    setProof((p) => ({ ...p, annotations: (p.annotations || []).filter((a) => a.id !== id) }));
  };

  const updateNotes = async (page, text) => {
    const { data } = await publicApi.put(`/public/proofs/${activeToken}/page-notes`, { page, text });
    setProof((p) => ({ ...p, page_notes: data.page_notes || {} }));
  };

  const deleteComment = async (id) => {
    await publicApi.delete(`/public/proofs/${activeToken}/comments/${id}`, {
      params: { author_name: authorName },
    });
    setProof((p) => ({ ...p, comments: (p.comments || []).filter((c) => c.id !== id) }));
  };

  // Client-side reply on a note thread. Sends the reply via the public
  // endpoint (no auth) — the backend fires an email to the designer.
  const addReply = async (commentId, payload) => {
    if (!authorName) { setShowNameModal(true); throw new Error("Please tell us your name first."); }
    const { data } = await publicApi.post(
      `/public/proofs/${activeToken}/comments/${commentId}/replies`,
      { text: payload.text, recipient_name: authorName },
    );
    setProof((p) => ({
      ...p,
      comments: (p.comments || []).map((c) =>
        c.id === commentId
          ? { ...c, replies: [...(c.replies || []), data] }
          : c
      ),
    }));
    return data;
  };

  // Upload an image to a note (client-side). Multipart form data; backend
  // enforces ≤ 3 per note, ≤ 8 MB each, image MIME only.
  const uploadAttachment = async (commentId, file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await publicApi.post(
      `/public/proofs/${activeToken}/comments/${commentId}/attachments`,
      fd,
      { headers: { "Content-Type": "multipart/form-data" } },
    );
    setProof((p) => ({
      ...p,
      comments: (p.comments || []).map((c) =>
        c.id === commentId
          ? { ...c, attachments: [...(c.attachments || []), data] }
          : c
      ),
    }));
    return data;
  };

  const deleteAttachment = async (commentId, attachmentId) => {
    await publicApi.delete(
      `/public/proofs/${activeToken}/comments/${commentId}/attachments/${attachmentId}`,
      { params: { author_name: authorName } },
    );
    setProof((p) => ({
      ...p,
      comments: (p.comments || []).map((c) =>
        c.id === commentId
          ? { ...c, attachments: (c.attachments || []).filter((a) => a.id !== attachmentId) }
          : c
      ),
    }));
  };

  const decide = async (decision) => {
    if (!authorName) { setShowNameModal(true); return; }
    setDecisionLoading(true);
    try {
      const { data } = await publicApi.post(`/public/proofs/${activeToken}/decision`, {
        decision, name: authorName,
      });
      setProof((p) => ({ ...p, status: data.status, decided_by: authorName, decided_at: new Date().toISOString() }));
      setFeedback(decision === "approved" ? "Thanks! The designer has been notified that you've approved this proof." : "Thanks — the designer has been notified that you've requested changes.");
      setThanksModal(decision);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not record your decision.");
    } finally {
      setDecisionLoading(false);
    }
  };

  // Clients can delete only their own notes/drawings (matched by name).
  // The studio (logged-in) uses a separate endpoint and bypasses this entirely.
  const canDelete = (c) => !proof?.archived && c.author_role === "client" && (c.author_name || "").trim().toLowerCase() === (authorName || "").trim().toLowerCase();
  const canDeleteAnnotation = (a) => !proof?.archived && a.author_role === "client" && (a.author_name || "").trim().toLowerCase() === (authorName || "").trim().toLowerCase();

  const sortedVersions = useMemo(() => proof?.versions?.slice().sort((a, b) => a.version - b.version) || [], [proof]);

  if (loading) {
    return <div className="min-h-screen grid place-items-center"><Spinner /></div>;
  }
  if (error && !proof) {
    return (
      <div className="min-h-screen grid place-items-center bg-zinc-50">
        <Card className="p-10 text-center max-w-md">
          <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
          <h1 className="font-display text-2xl font-bold mb-2">Proof not found</h1>
          <p className="text-sm text-zinc-500">{error}</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Top bar */}
      <header className="bg-white border-b border-zinc-200 sticky top-0 z-30">
        <div className="px-4 sm:px-6 py-5 flex items-center justify-between gap-4 flex-wrap max-w-[1400px] mx-auto">
          <div className="flex items-center gap-5 min-w-0">
            <img
              src="/kerboom-logo.png"
              alt="Kerboom"
              className="h-12 sm:h-14 w-auto shrink-0"
              data-testid="proof-header-logo"
            />
            <div className="min-w-0 border-l border-zinc-200 pl-5">
              <h1 className="font-display font-bold text-zinc-950 text-2xl sm:text-3xl leading-tight tracking-tight">Your Kerboom Proof</h1>
              <p className="text-xs sm:text-sm text-zinc-500 mt-0.5 truncate">Sent by {proof.designer_name} · Kerboom Design</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {sortedVersions.length > 1 && (
              <select
                className="text-xs border border-zinc-200 rounded-full px-3 py-1.5 bg-white"
                value={activeToken}
                onChange={(e) => setActiveToken(e.target.value)}
                data-testid="proof-version-select"
              >
                {sortedVersions.map((v) => (
                  <option key={v.id} value={v.share_token}>
                    v{v.version}{v.archived ? " (archived)" : ""}
                  </option>
                ))}
              </select>
            )}
            <StatusBadge status={proof.status} />
            {authorName && (
              <span className="text-xs text-zinc-500 hidden sm:inline">Reviewing as <strong className="text-zinc-900">{authorName}</strong> <button onClick={() => setShowNameModal(true)} className="underline ml-1 hover:text-zinc-700">change</button></span>
            )}
          </div>
        </div>
      </header>

      {/* Title block */}
      <div className="px-4 sm:px-6 pt-6 pb-2 max-w-[1400px] mx-auto">
        <p className="text-xs uppercase tracking-widest text-zinc-500 font-semibold">{proof.client_name}</p>
        <h2 className="font-display text-2xl font-bold text-zinc-950 mt-1">{proof.name}</h2>
        <p className="text-sm text-zinc-500 mt-1">
          {proof.project_name} · v{proof.version}{proof.archived ? " (archived)" : ""} · uploaded {formatDateUK(proof.created_at)}
        </p>

        {feedback && (
          <div className="mt-4 inline-flex items-center gap-2 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-full px-4 py-2 text-sm">
            <Check className="w-4 h-4" />{feedback}
          </div>
        )}
        {proof.archived && (
          <div className="mt-4 inline-flex items-center gap-2 bg-zinc-200/60 text-zinc-700 rounded-full px-4 py-2 text-xs">
            This is an older archived version — viewing in read-only mode.
          </div>
        )}
        {proof.decided_by && !feedback && (
          <p className="text-xs text-zinc-500 mt-3">
            {proof.status === "Approved" ? "Approved" : "Changes requested"} by <strong className="text-zinc-700">{proof.decided_by}</strong> on {formatDateUK(proof.decided_at)}.
          </p>
        )}
      </div>

      {/* Viewer */}
      <div className="px-4 sm:px-6 pb-8 max-w-[1400px] mx-auto">
        <div className="h-[calc(100vh-260px)] min-h-[600px]">
          <PdfProofViewer
            fileUrl={fileUrl}
            comments={proof.comments || []}
            annotations={proof.annotations || []}
            pageNotes={proof.page_notes || {}}
            onAddComment={addComment}
            onAddAnnotation={addAnnotation}
            onDeleteAnnotation={deleteAnnotation}
            onUpdatePageNotes={updateNotes}
            onDeleteComment={deleteComment}
            onAddReply={addReply}
            replyRole="client"
            onUploadAttachment={uploadAttachment}
            onDeleteAttachment={deleteAttachment}
            attachmentBaseUrl={attachmentBaseUrl}
            canEdit={!proof.archived}
            canDelete={canDelete}
            canDeleteAnnotation={canDeleteAnnotation}
            authorName={authorName}
            downloadUrl={`${fileUrl}?download=1`}
            downloadFilename={proof.original_filename || `${proof.name || "proof"}.pdf`}
            downloadSizeLabel={proof.file_size ? formatFileSize(proof.file_size) : ""}
          />
        </div>

        {/* Approval bar */}
        {!proof.archived && (
          <div className="sticky bottom-4 mt-6 bg-white border-2 border-zinc-300 shadow-xl rounded-2xl p-5 flex items-start justify-between gap-4 flex-wrap" data-testid="proof-approval-bar">
            <div className="flex-1 min-w-[260px]">
              <p className="text-sm font-bold text-zinc-950">
                {(proof.comments?.length || 0) + (proof.annotations?.length || 0) > 0
                  ? "Done reviewing? Send your decision to the designer."
                  : "Approve this proof — or add notes/drawings first, then send your decision."}
              </p>
              <p className="text-xs text-zinc-600 mt-1">
                You've added <strong className="text-zinc-900">{proof.comments?.length || 0} note{(proof.comments?.length || 0) !== 1 ? "s" : ""}</strong> and <strong className="text-zinc-900">{proof.annotations?.length || 0} drawing{(proof.annotations?.length || 0) !== 1 ? "s" : ""}</strong>. Notes save automatically as you type — your decision below tells the designer you're finished.
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                type="button"
                onClick={() => decide("changes_requested")}
                disabled={decisionLoading}
                className="px-5 py-2.5 rounded-full text-sm font-semibold bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-50 inline-flex items-center gap-1.5 shadow-sm"
                data-testid="proof-request-changes-btn"
              >
                <Pencil className="w-4 h-4" />Submit my changes
              </button>
              <button
                type="button"
                onClick={() => decide("approved")}
                disabled={decisionLoading}
                className="px-5 py-2.5 rounded-full text-sm font-semibold bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 inline-flex items-center gap-1.5 shadow-sm"
                data-testid="proof-approve-btn"
              ><Check className="w-4 h-4" />Approve as-is</button>
            </div>
          </div>
        )}
      </div>

      {/* Name capture modal */}
      {showNameModal && (
        <div className="fixed inset-0 z-50 grid place-items-center p-4 animate-fade-in">
          <div className="absolute inset-0 bg-zinc-950/60 backdrop-blur-sm" />
          <Card className="relative p-6 w-full max-w-sm">
            <h2 className="font-display text-xl font-semibold mb-1">Who's reviewing?</h2>
            <p className="text-sm text-zinc-500 mb-4">Add your name so the designer knows whose notes are whose.</p>
            <NameForm initial={authorName} onSave={saveName} />
          </Card>
        </div>
      )}

      {/* Thanks confirmation popup after submitting a decision */}
      <Dialog open={!!thanksModal} onOpenChange={(o) => !o && setThanksModal(null)}>
        <DialogContent className="sm:max-w-md" data-testid="proof-thanks-modal">
          <DialogHeader>
            <div className="mx-auto w-14 h-14 rounded-full bg-emerald-100 grid place-items-center mb-3">
              <CheckCircle2 className="w-8 h-8 text-emerald-600" />
            </div>
            <DialogTitle className="text-center font-display text-2xl">
              Thanks for submitting your proof
            </DialogTitle>
            <DialogDescription className="text-center text-sm text-zinc-600 pt-2">
              {thanksModal === "approved"
                ? "The studio has been notified that you've approved this proof."
                : "The studio has been notified and will action your changes shortly."}
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-center pt-2">
            <Btn onClick={() => setThanksModal(null)} data-testid="proof-thanks-close">
              Close
            </Btn>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const NameForm = ({ initial, onSave }) => {
  const [n, setN] = useState(initial || "");
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSave(n); }}>
      <Label>Your name</Label>
      <Input autoFocus value={n} onChange={(e) => setN(e.target.value)} placeholder="e.g. Henry Davenport" data-testid="proof-name-input" />
      <div className="flex justify-end mt-4">
        <Btn type="submit" disabled={!n.trim()} data-testid="proof-name-save">
          Continue<ArrowRight className="w-4 h-4" />
        </Btn>
      </div>
    </form>
  );
};

export default ProofPage;
